import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import type { CondicionCuestionario, HistorialClinico, RespuestaCondicion } from '@api/Consents/Consent';
import {
    borradorDesde,
    condicionesSinResponder,
    ETIQUETA_RESPUESTA,
    HistorialBorrador,
    historialParaEnviar,
    restoEnNo
} from '@src/shared/utils/consent';
import { Dato } from './common';

const RESPUESTAS: RespuestaCondicion[] = ['si', 'no', 'controlada'];

/** Lo que se ve del historial ya guardado: domicilio y solo las condiciones que no son «No». */
export const ResumenHistorial: React.FC<{
    historial: HistorialClinico;
    condiciones: CondicionCuestionario[];
}> = ({ historial, condiciones }) => {
    const marcadas = condiciones.filter(
        (c) => historial.condiciones[c.clave] && historial.condiciones[c.clave] !== 'no'
    );
    return (
        <Stack spacing={1.25}>
            {historial.domicilio && <Dato etiqueta="Domicilio">{historial.domicilio}</Dato>}
            {historial.colonia && <Dato etiqueta="Colonia">{historial.colonia}</Dato>}
            {historial.actividad && <Dato etiqueta="Actividad">{historial.actividad}</Dato>}
            <Stack spacing={0.75}>
                <Typography variant="body2" color="text.secondary">
                    Condiciones
                </Typography>
                {marcadas.length === 0 ? (
                    <Typography variant="body2">Ninguna: respondió «No» a todas.</Typography>
                ) : (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {marcadas.map((c) => (
                            <Chip
                                key={c.clave}
                                size="small"
                                color={
                                    c.consentimientoMedico && historial.condiciones[c.clave] === 'si'
                                        ? 'warning'
                                        : 'default'
                                }
                                label={`${c.etiqueta}: ${ETIQUETA_RESPUESTA[historial.condiciones[c.clave]]}`}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
            {historial.medicamento && <Dato etiqueta="Medicamento o tratamiento">{historial.medicamento}</Dato>}
            {historial.comentarios && <Dato etiqueta="Comentarios">{historial.comentarios}</Dato>}
        </Stack>
    );
};

/**
 * El cuestionario del formato en papel (página 1). Las 14 condiciones se responden una por una
 * (sí, no o controlada): el server no acepta una sin responder. Las de asterisco piden
 * consentimiento médico; se señalan, no bloquean (pregunta f de la Etapa 0, sin respuesta aún).
 */
export const FormularioHistorial: React.FC<{
    historial: HistorialClinico | null;
    condiciones: CondicionCuestionario[];
    esClienta: boolean;
    guardando: boolean;
    onGuardar: (historial: HistorialClinico) => void;
    onCancelar?: () => void;
}> = ({ historial, condiciones, esClienta, guardando, onGuardar, onCancelar }) => {
    const claves = useMemo(() => condiciones.map((c) => c.clave), [condiciones]);
    const [borrador, setBorrador] = useState<HistorialBorrador>(() => borradorDesde(historial, claves));
    const [intentado, setIntentado] = useState(false);
    const faltan = condicionesSinResponder(borrador, claves);

    const campo = (clave: 'domicilio' | 'colonia' | 'actividad' | 'medicamento' | 'comentarios') => ({
        value: borrador[clave],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const valor = e.target.value;
            setBorrador((b) => ({ ...b, [clave]: valor }));
        }
    });

    const guardar = () => {
        setIntentado(true);
        if (faltan.length > 0) return;
        onGuardar(historialParaEnviar(borrador));
    };

    return (
        <Stack spacing={2.5}>
            <Stack spacing={1.5}>
                <TextField label="Domicilio" fullWidth autoComplete="street-address" {...campo('domicilio')} />
                <TextField label="Colonia" fullWidth {...campo('colonia')} />
                <TextField
                    label={esClienta ? '¿A qué te dedicas?' : 'Actividad o lugar de trabajo'}
                    placeholder={esClienta ? 'Tu actividad o lugar de trabajo' : undefined}
                    fullWidth
                    {...campo('actividad')}
                />
            </Stack>

            <Divider />

            <Stack spacing={0.5}>
                <Typography fontWeight={600}>
                    {esClienta ? '¿Tienes alguna de estas condiciones?' : 'Condiciones'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Responde cada una. Las marcadas con * requieren consentimiento médico para el procedimiento.
                </Typography>
            </Stack>

            <Stack divider={<Divider flexItem />} spacing={1}>
                {condiciones.map((c) => {
                    const sinResponder = intentado && !borrador.condiciones[c.clave];
                    return (
                        <Stack
                            key={c.clave}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            useFlexGap
                            spacing={1}
                        >
                            <Typography
                                sx={{ flex: '1 1 120px', minWidth: 0 }}
                                color={sinResponder ? 'error' : 'text.primary'}
                                fontWeight={sinResponder ? 600 : 400}
                            >
                                {c.etiqueta}
                                {c.consentimientoMedico ? ' *' : ''}
                            </Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={borrador.condiciones[c.clave] ?? null}
                                onChange={(_e, valor: RespuestaCondicion | null) => {
                                    if (!valor) return;
                                    setBorrador((b) => ({ ...b, condiciones: { ...b.condiciones, [c.clave]: valor } }));
                                }}
                                aria-label={c.etiqueta}
                            >
                                {RESPUESTAS.map((r) => (
                                    <ToggleButton key={r} value={r} sx={{ px: 1.5, py: 0.5, minWidth: 44 }}>
                                        {ETIQUETA_RESPUESTA[r]}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Stack>
                    );
                })}
            </Stack>

            {faltan.length > 0 && faltan.length < claves.length && (
                <Box>
                    <Button size="small" onClick={() => setBorrador((b) => restoEnNo(b, claves))}>
                        Marcar las {faltan.length} que faltan en «No»
                    </Button>
                </Box>
            )}

            {condiciones.some((c) => c.consentimientoMedico && borrador.condiciones[c.clave] === 'si') ? (
                <Alert severity="warning">
                    {esClienta
                        ? 'Para esta condición se requiere consentimiento médico. Tu artista lo revisará contigo antes de empezar.'
                        : 'Hay una condición que requiere consentimiento médico: revísalo con la clienta antes de empezar.'}
                </Alert>
            ) : null}

            <Divider />

            <TextField
                label={esClienta ? '¿Tomas algún medicamento o llevas algún tratamiento?' : 'Medicamento o tratamiento'}
                placeholder="Escribe cuál, o déjalo vacío si ninguno"
                fullWidth
                multiline
                minRows={2}
                {...campo('medicamento')}
            />
            <TextField label="Comentarios" fullWidth multiline minRows={2} {...campo('comentarios')} />

            {intentado && faltan.length > 0 && (
                <Alert severity="error">
                    Falta responder {faltan.length === 1 ? '1 condición' : `${faltan.length} condiciones`}.
                </Alert>
            )}

            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
                {onCancelar && (
                    <Button onClick={onCancelar} disabled={guardando} color="inherit">
                        Cancelar
                    </Button>
                )}
                <Button variant="contained" size="large" onClick={guardar} disabled={guardando}>
                    {guardando ? 'Guardando…' : 'Guardar historial'}
                </Button>
            </Stack>
        </Stack>
    );
};
